// src/components/pages/SignInPage.js
import React, { useState, useEffect } from "react";
import { useTheme } from "styled-components";
import { useNavigate, useLocation } from "react-router-dom";
import {
  signInUserWithEmailPassword,
  signInWithGoogle,
  signInWithApple,
  getFriendlyErrorMessage,
} from "../../../services/authService";
import {
  Backdrop,
  Box,
  CircularProgress,
  Typography,
  Container,
} from "@mui/material";
import { TOP_APP_BAR_HEIGHT } from "../../../constants/constants";
import SignInForm from "../../organisms/SignInForm";
import useStore from "../../../store/useStore";
import useAuthStore from "../../../store/useAuthStore";
import useUserProfile from "../../../hooks/useUserProfile";
import { fetchUserProfileFromFirestore } from "../../../services/profileService";
import { getOrCreateUserProfile } from "../../../services/profileService";

const SignInPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { setUser } = useAuthStore();
  const { setUserProfile } = useStore();
  const [authenticatedUser, setAuthenticatedUser] = useState(null);
  const isConnectRoute = location.pathname === "/connect";
  const [pairingCode, setPairingCode] = useState("");

  // Extract pairing code from URL if available
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const codeFromUrl = queryParams.get("code");
    if (codeFromUrl) {
      const upperCaseCode = codeFromUrl.toUpperCase();
      setPairingCode(upperCaseCode);
      console.log("Pairing code from URL:", upperCaseCode);
    }
  }, [location.search]);

  const handleCodeComplete = (completeCode) => {
    setPairingCode(completeCode);
    console.log("Complete pairing code from SignInForm:", completeCode);
  };

  // Determine where to redirect after sign-in
  const from = location.state?.from?.pathname || "/";

  // Use custom hook to fetch user profile (if any)
  const {
    // userProfile,
    loading: profileLoading,
    error: profileError,
  } = useUserProfile(authenticatedUser ? authenticatedUser.uid : null);

  // After authentication and profile fetch, decide where to go.
  useEffect(() => {
    const redirectAfterProfileCheck = async () => {
      if (authenticatedUser) {
        // Try fetching the profile from Firestore
        const firestoreProfile = await fetchUserProfileFromFirestore(
          authenticatedUser.uid
        );
        if (firestoreProfile) {
          // If profile exists, store it
          setUserProfile(firestoreProfile);
          // If the profile has a phone number, proceed to home.
          // Otherwise, send the user to the OTP page.
          if (firestoreProfile.phoneNumber) {
            navigate(from, { replace: true });
          } else {
            navigate("/phone-otp", { replace: true });
          }
        } else {
          // No profile exists: create one (or use getOrCreate helper)
          const newProfile = await getOrCreateUserProfile(authenticatedUser);
          setUserProfile(newProfile);
          // Even if the new profile doesn't have a phone number, send user to OTP page.
          if (newProfile.phoneNumber) {
            navigate(from, { replace: true });
          } else {
            navigate("/phone-otp", { replace: true });
          }
        }
        setLoading(false);
      }
      if (profileError) {
        setError("Error fetching user profile.");
        setLoading(false);
      }
    };

    // Only run this redirect if profile loading is complete.
    if (!profileLoading) {
      redirectAfterProfileCheck();
    }
  }, [
    profileLoading,
    authenticatedUser,
    setUserProfile,
    navigate,
    from,
    profileError,
  ]);

  const handleEmailPasswordSignIn = async (email, password) => {
    setLoading(true);
    setError(null);
    try {
      const user = await signInUserWithEmailPassword(
        email,
        password,
        pairingCode,
        isConnectRoute
      );
      setUser(user);
      setAuthenticatedUser(user);
    } catch (error) {
      const friendlyMessage = getFriendlyErrorMessage(error.code);
      setError(friendlyMessage);
      console.error("Error signing in with email and password:", error.message);
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setLoading(true);
    setError(null);
    try {
      const user = await signInWithGoogle(pairingCode, isConnectRoute);
      setUser(user);
      setAuthenticatedUser(user);
    } catch (error) {
      const friendlyMessage = getFriendlyErrorMessage(error.code);
      setError(friendlyMessage);
      console.error("Error signing in with Google:", error.message);
      setLoading(false);
    }
  };

  const handleAppleSignIn = async () => {
    setLoading(true);
    setError(null);
    try {
      const user = await signInWithApple(pairingCode, isConnectRoute);
      setUser(user);
      setAuthenticatedUser(user);
    } catch (error) {
      const friendlyMessage = getFriendlyErrorMessage(error.code);
      setError(friendlyMessage);
      console.error("Error signing in with Apple:", error.message);
      setLoading(false);
    }
  };

  return (
    <>
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <CircularProgress color="inherit" />
          <Typography variant="h6" sx={{ mt: 2 }}>
            Signing in...
          </Typography>
        </Box>
      </Backdrop>

      <Container
        maxWidth="xs"
        sx={{
          backgroundColor: "white",
          height: `calc(100vh - ${TOP_APP_BAR_HEIGHT}px`
        }}
      >
        <SignInForm
          theme={theme}
          pair={isConnectRoute}
          pairingCode={pairingCode}
          handleEmailPasswordSignIn={handleEmailPasswordSignIn}
          handleGoogleSignIn={handleGoogleSignIn}
          handleAppleSignIn={handleAppleSignIn}
          onCodeComplete={handleCodeComplete}
          loading={loading}
          error={error}
        />
      </Container>
    </>
  );
};

export default SignInPage;
